import React, { useMemo } from "react";
import { buildTaEgoReport, getEgoStateName } from "./taEgoReportEngine";
import { formatDuration } from "../utils/formatDuration";

const EGO_COLORS = {
  parent: "#f97316",
  adult: "#0ea5e9",
  child: "#22c55e"
};

export default function TaEgoPersonalReport({
  scores,
  userInfo,
  timestamp,
  durationMs,
  language = "tr",
  accessContext,
  // true → kendi kapağını ve künye ızgarasını ATLAR; baskı katmanı antetli bir
  // kapak zaten basıyor, ikisi birlikte sayfada iki başlık üretiyordu.
  baskiModu = false
}) {
  const lang = language === "en" ? "en" : "tr";
  const isTR = lang === "tr";
  // Yüzde işaretinin yeri dile bağlı: TR "%100", EN "100%".
  const yuzde = (n) => (isTR ? `%${n}` : `${n}%`);
  const durationText = formatDuration(durationMs, lang);

  const participantName =
    [userInfo?.name, userInfo?.surname].filter(Boolean).join(" ").trim() ||
    userInfo?.fullName ||
    userInfo?.adSoyad ||
    userInfo?.participantName ||
    (isTR ? "Katılımcı" : "Participant");

  const report = useMemo(
    () =>
      buildTaEgoReport({
        scores,
        participant: {
          name: participantName,
          company: accessContext?.companyName || "",
          team: accessContext?.teamName || "",
          generationRange: userInfo?.generationRange || "",
          roleLevel: userInfo?.roleLevel || ""
        },
        language: lang
      }),
    [scores, participantName, accessContext, userInfo, lang]
  );

  const text = {
    tr: {
      eyebrow: "SOMEREN LABS · TA / 3 EGO",
      title: "Kişiselleştirilmiş Ego Durumu Raporu",
      intro:
        "Bu rapor, katılımcının Ebeveyn, Yetişkin ve Çocuk Ego durumlarını iş davranışı, iletişim, karar alma ve gelişim odağı açısından yorumlar. Amaç kişiyi etiketlemek değil; karar ve etkileşim örüntülerini daha görünür hale getirmektir.",
      participant: "Katılımcı",
      company: "Şirket",
      team: "Takım",
      date: "Tarih",
      duration: "Tamamlama Süresi",
      executive: "Yönetici Özeti",
      balance: "Ego Dengesi Yorumu",
      workStyle: "İş Yaşamı Davranış Yorumu",
      scores: "Ego Durumu Skorları",
      strengths: "Güçlü Sinyaller",
      watchouts: "Dikkat Alanları",
      communication: "İletişim Önerileri",
      manager: "Yönetici İçin Okuma",
      hr: "İK İçin Okuma",
      development: "30 Günlük Gelişim Odağı",
      dimensions: "Ego Durumu Bazlı Kısa Yorum",
      note:
        "Not: TA / 3 Ego sonuçları klinik teşhis, seçim veya tek başına performans yargısı için değil; gelişim, farkındalık, iletişim ve takım dengesi amacıyla kullanılmalıdır."
    },
    en: {
      eyebrow: "SOMEREN LABS · TA / 3 EGO",
      title: "Personalised Ego State Report",
      intro:
        "This report interprets the participant’s Parent, Adult and Child Ego states through work behaviour, communication, decision-making and development focus. The aim is not to label the person, but to make decision and interaction patterns more visible.",
      participant: "Participant",
      company: "Company",
      team: "Team",
      date: "Date",
      duration: "Completion Time",
      executive: "Executive Snapshot",
      balance: "Ego Balance Insight",
      workStyle: "Work Behaviour Interpretation",
      scores: "Ego State Scores",
      strengths: "Strong Signals",
      watchouts: "Watch-outs",
      communication: "Communication Tips",
      manager: "Manager Lens",
      hr: "HR Lens",
      development: "30-Day Development Focus",
      dimensions: "Ego State-Based Brief Insight",
      note:
        "Note: TA / 3 Ego results should be used for development, awareness, communication and team balance; not as a clinical diagnosis, selection or standalone performance judgement tool."
    }
  }[lang];

  return (
    <section style={baskiModu ? styles.shellBaski : styles.shell} className={baskiModu ? "sl-akis" : undefined}>
      {!baskiModu && (
        <>
          <div style={styles.hero}>
            <p style={styles.eyebrow}>{text.eyebrow}</p>
            <h2 style={styles.title}>{text.title}</h2>
            <p style={styles.intro}>{text.intro}</p>
          </div>

          <div style={styles.metaGrid}>
            <Meta label={text.participant} value={participantName} />
            <Meta label={text.company} value={accessContext?.companyName || "-"} />
            <Meta label={text.team} value={accessContext?.teamName || "-"} />
            <Meta label={text.date} value={timestamp || "-"} />
            <Meta label={text.duration} value={durationText} />
          </div>
        </>
      )}

      <div style={styles.summaryGrid} className="sl-izgara">
        <Card title={text.executive} featured>
          <p style={styles.paragraph}>{report.executiveSummary}</p>
        </Card>

        <Card title={text.balance}>
          <p style={styles.paragraph}>{report.egoBalanceInsight}</p>
        </Card>

        <Card title={text.workStyle}>
          <p style={styles.paragraph}>{report.workStyle}</p>
        </Card>
      </div>

      <Card title={text.scores}>
        <div style={styles.scoreGrid} className="sl-izgara">
          {["parent", "adult", "child"].map((key) => (
            <ScoreRow key={key} egoKey={key} value={report.scores[key]} lang={lang} />
          ))}
        </div>
      </Card>

      <div style={styles.summaryGrid} className="sl-izgara">
        <ListCard title={text.strengths} items={report.strengths} />
        <ListCard title={text.watchouts} items={report.watchouts} />
        <ListCard title={text.communication} items={report.communicationTips} />
        <ListCard title={text.manager} items={report.managerNotes} />
        <ListCard title={text.hr} items={report.hrNotes} />
        <ListCard title={text.development} items={report.developmentPlan} />
      </div>

      <Card title={text.dimensions}>
        <div style={styles.dimensionGrid} className="sl-izgara">
          {report.sections.map((section) => (
            <div key={section.key} style={styles.dimensionCard}>
              <div style={styles.dimensionTop}>
                <span
                  style={{
                    ...styles.egoBadge,
                    background: EGO_COLORS[section.key]
                  }}
                >
                  {yuzde(section.value)}
                </span>
                <strong>{section.content.title}</strong>
              </div>
              <p style={styles.smallText}>{section.content.headline}</p>
              <p style={styles.smallText}>{section.content.interpretation}</p>
              <p style={styles.roleText}>{section.content.role}</p>
            </div>
          ))}
        </div>
      </Card>

      <p style={styles.note}>{text.note}</p>
    </section>
  );
}

function Meta({ label, value }) {
  return (
    <div style={styles.meta}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Card({ title, children, featured = false }) {
  return (
    <article style={{ ...styles.card, ...(featured ? styles.featuredCard : {}) }}>
      <h3 style={styles.cardTitle}>{title}</h3>
      {children}
    </article>
  );
}

function ListCard({ title, items }) {
  return (
    <Card title={title}>
      <ul style={styles.list}>
        {items.map((item, index) => (
          <li key={index} style={styles.listItem}>
            {item}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ScoreRow({ egoKey, value, lang }) {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));

  return (
    <div style={styles.scoreRow}>
      <span style={{ ...styles.scoreLabel, color: EGO_COLORS[egoKey] }}>
        {getEgoStateName(egoKey, lang)}
      </span>
      <div style={styles.scoreTrack}>
        <div
          style={{
            ...styles.scoreFill,
            width: `${safeValue}%`,
            background: EGO_COLORS[egoKey]
          }}
        />
      </div>
      <strong style={styles.scoreValue}>
        {lang === "en" ? `${safeValue}%` : `%${safeValue}`}
      </strong>
    </div>
  );
}

const styles = {
  // Baskıda kabuk gölgesiz/dolgusuz: antet çerçeveyi zaten kuruyor.
  shellBaski: { display: "grid", gap: 14, background: "transparent", border: 0,
    borderRadius: 0, padding: 0, boxShadow: "none" },
  shell: {
    marginTop: 34,
    textAlign: "left",
    color: "#111827"
  },
  hero: {
    borderRadius: 24,
    padding: 28,
    background: "linear-gradient(135deg, #111827 0%, #334155 62%, #0ea5e9 145%)",
    color: "#ffffff",
    marginBottom: 16
  },
  eyebrow: {
    margin: "0 0 10px",
    fontSize: 12,
    letterSpacing: "0.16em",
    fontWeight: 900,
    opacity: 0.88
  },
  title: {
    margin: "0 0 12px",
    fontSize: 30,
    lineHeight: 1.15,
    color: "#ffffff"
  },
  intro: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.6,
    opacity: 0.92
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 16
  },
  meta: {
    display: "grid",
    gap: 4,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 14
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 14,
    marginBottom: 14
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    padding: 18,
    marginBottom: 14,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)"
  },
  featuredCard: {
    background: "#eff6ff",
    borderColor: "#bfdbfe"
  },
  cardTitle: {
    margin: "0 0 10px",
    fontSize: 18,
    lineHeight: 1.25,
    color: "#111827"
  },
  paragraph: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.65,
    color: "#374151"
  },
  scoreGrid: {
    display: "grid",
    gap: 12
  },
  scoreRow: {
    display: "grid",
    gridTemplateColumns: "150px 1fr 48px",
    gap: 12,
    alignItems: "center"
  },
  scoreLabel: {
    fontSize: 14,
    fontWeight: 900
  },
  scoreTrack: {
    height: 12,
    background: "#e5e7eb",
    borderRadius: 999,
    overflow: "hidden"
  },
  scoreFill: {
    height: "100%",
    borderRadius: 999
  },
  scoreValue: {
    fontSize: 14,
    textAlign: "right",
    color: "#111827"
  },
  list: {
    margin: 0,
    paddingLeft: 0,
    listStyle: "none"
  },
  listItem: {
    marginBottom: 10,
    fontSize: 14,
    lineHeight: 1.6,
    color: "#111827",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "14px 16px"
  },
  dimensionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: 12
  },
  dimensionCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 14,
    background: "#f9fafb"
  },
  dimensionTop: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
    color: "#111827"
  },
  egoBadge: {
    minWidth: 42,
    height: 28,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontWeight: 900,
    fontSize: 12
  },
  smallText: {
    margin: "8px 0 0",
    fontSize: 13,
    lineHeight: 1.55,
    color: "#4b5563"
  },
  roleText: {
    margin: "8px 0 0",
    fontSize: 13,
    lineHeight: 1.55,
    color: "#111827",
    fontWeight: 700
  },
  note: {
    margin: "14px 0 0",
    fontSize: 12,
    lineHeight: 1.5,
    color: "#6b7280"
  }
};
